import { useCallback, useEffect, useMemo, useState } from "react";
import { jfetch } from "../api";

/**
 * CP-15 — Findings / Challenger UI: der missionsweite Finding-Ledger als
 * Operator-Flaeche. Reines Read-Model — die Schreib-Autoritaet liegt bei der
 * Missions-Runtime (MissionFindingsRepo + DB-Trigger: found_by immutable,
 * kein Severity-Downgrade, kein Self-Close, Fix-Evidence + Reattack-Verdict
 * eines unabhaengigen Pruefers Pflicht). Diese Flaeche erzeugt keine eigene
 * Finding-Authority: kein Oeffnen, kein Schliessen, kein Downgrade.
 */

type Severity = "INFO" | "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
type FindingStatus = "OPEN" | "FIX_PROPOSED" | "FIX_APPLIED" | "REATTACKED" | "CLOSED" | "WONT_FIX";

interface FindingView {
  finding_id: string;
  mission_id: string;
  thread_id: string | null;
  challenge_message_id: string | null;
  severity: Severity;
  finding_class: string;
  found_by: string;
  challenge_fingerprint: string | null;
  status: FindingStatus;
  fixed_by: string | null;
  fix_evidence_id: string | null;
  reattack_verdict_message_id: string | null;
  closed_by: string | null;
  created_at: string;
  updated_at: string;
  mission_goal: string | null;
}

interface LedgerSummary {
  openBySeverity: Record<Severity, number>;
  openTotal: number;
  closedTotal: number;
  wontFixTotal: number;
  byStatus: Record<FindingStatus, number>;
  total: number;
}

interface LedgerResponse {
  ok: boolean;
  summary: LedgerSummary;
  findings: FindingView[];
}

const SEVERITIES: Severity[] = ["CRITICAL", "HIGH", "MEDIUM", "LOW", "INFO"];
const STATUSES: FindingStatus[] = ["OPEN", "FIX_PROPOSED", "FIX_APPLIED", "REATTACKED", "CLOSED", "WONT_FIX"];

function severityChipKlasse(severity: Severity): string {
  switch (severity) {
    case "CRITICAL":
      return "border-rose-500/50 bg-rose-500/15 text-rose-200";
    case "HIGH":
      return "border-orange-500/50 bg-orange-500/10 text-orange-300";
    case "MEDIUM":
      return "border-amber-500/50 bg-amber-500/10 text-amber-300";
    case "LOW":
      return "border-sky-500/40 bg-sky-500/10 text-sky-300";
    default:
      return "border-jarvis-border bg-jarvis-bg/60 text-jarvis-muted";
  }
}

function statusChipKlasse(status: FindingStatus): string {
  switch (status) {
    case "OPEN":
      return "border-rose-500/40 bg-rose-500/10 text-rose-300";
    case "FIX_PROPOSED":
    case "FIX_APPLIED":
      return "border-amber-500/40 bg-amber-500/10 text-amber-300";
    case "REATTACKED":
      return "border-sky-500/40 bg-sky-500/10 text-sky-300";
    case "CLOSED":
      return "border-emerald-500/40 bg-emerald-500/10 text-emerald-300";
    default:
      return "border-jarvis-border bg-jarvis-bg/60 text-jarvis-muted";
  }
}

/** Reattack-Zustand abgeleitet aus Ledger-Feldern (nur Anzeige, keine Autoritaet). */
function reattackText(f: FindingView): string {
  if (f.reattack_verdict_message_id) return "Reattack belegt (Verdict)";
  if (f.status === "REATTACKED") return "Reattack läuft";
  if (f.status === "FIX_APPLIED") return "Reattack ausstehend";
  if (f.status === "CLOSED") return "— (ungültig ohne Verdict)";
  return "—";
}

export function FindingsLedgerPanel(): JSX.Element {
  const [daten, setDaten] = useState<LedgerResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [offen, setOffen] = useState<string | null>(null);
  const [severityFilter, setSeverityFilter] = useState<Severity[]>([]);
  const [statusFilter, setStatusFilter] = useState<FindingStatus[]>([]);
  const [suche, setSuche] = useState("");
  const [missionFilter, setMissionFilter] = useState("");

  const query = useMemo(() => {
    const teile: string[] = [];
    if (severityFilter.length > 0) teile.push("severity=" + encodeURIComponent(severityFilter.join(",")));
    if (statusFilter.length > 0) teile.push("status=" + encodeURIComponent(statusFilter.join(",")));
    if (missionFilter.trim()) teile.push("mission_id=" + encodeURIComponent(missionFilter.trim()));
    if (suche.trim()) teile.push("q=" + encodeURIComponent(suche.trim()));
    return teile.length > 0 ? "?" + teile.join("&") : "";
  }, [severityFilter, statusFilter, missionFilter, suche]);

  const load = useCallback((): void => {
    setLoading(true);
    setError(null);
    jfetch<LedgerResponse>("/api/findings" + query)
      .then((r) => setDaten(r))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [query]);
  useEffect(load, [load]);

  const toggle = <T,>(menge: T[], wert: T, setMenge: (m: T[]) => void): void => {
    setMenge(menge.includes(wert) ? menge.filter((m) => m !== wert) : [...menge, wert]);
  };

  const summary = daten?.summary;

  return (
    <div className="space-y-3" aria-label="Findings und Challenger" data-testid="findings-ledger-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Findings &amp; Challenger</h2>
          <p className="text-xs text-jarvis-muted mt-0.5">
            Missionsweiter Ledger — Schließen nur über die Missions-Runtime (unabhängiger Prüfer + Fix-Evidence +
            Reattack); diese Fläche ist rein lesend.
          </p>
        </div>
        <button className="btn" onClick={load} disabled={loading}>
          {loading ? "Lade…" : "Aktualisieren"}
        </button>
      </div>

      {error && (
        <div className="card p-4 text-rose-300 text-sm" role="alert">
          Findings nicht abrufbar: {error}
        </div>
      )}

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-7 gap-2" data-testid="findings-summary">
          {SEVERITIES.map((sev) => (
            <div key={sev} className={`card p-2.5 border ${severityChipKlasse(sev)}`}>
              <div className="text-[10px] uppercase tracking-wider">{sev} offen</div>
              <div className="text-xl font-semibold mt-0.5" data-testid={"findings-open-" + sev}>
                {summary.openBySeverity[sev] ?? 0}
              </div>
            </div>
          ))}
          <div className="card p-2.5">
            <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">Geschlossen</div>
            <div className="text-xl font-semibold mt-0.5" data-testid="findings-closed-total">
              {summary.closedTotal}
            </div>
            <div className="text-[10px] text-jarvis-muted">{summary.wontFixTotal} WONT_FIX</div>
          </div>
          <div className="card p-2.5">
            <div className="text-[10px] uppercase tracking-wider text-jarvis-muted">Gesamt</div>
            <div className="text-xl font-semibold mt-0.5" data-testid="findings-total">
              {summary.total}
            </div>
            <div className="text-[10px] text-jarvis-muted">{summary.openTotal} offen</div>
          </div>
        </div>
      )}

      <div className="card p-3 space-y-2">
        <div className="flex flex-wrap gap-1.5" aria-label="Severity-Filter">
          {SEVERITIES.map((sev) => (
            <button
              key={sev}
              className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                severityFilter.includes(sev) ? severityChipKlasse(sev) : "border-jarvis-border text-jarvis-muted hover:text-jarvis-text"
              }`}
              data-testid={"findings-filter-severity-" + sev}
              aria-pressed={severityFilter.includes(sev)}
              onClick={() => toggle(severityFilter, sev, setSeverityFilter)}
            >
              {sev}
            </button>
          ))}
          <span className="w-px bg-jarvis-border mx-1" />
          {STATUSES.map((st) => (
            <button
              key={st}
              className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                statusFilter.includes(st) ? statusChipKlasse(st) : "border-jarvis-border text-jarvis-muted hover:text-jarvis-text"
              }`}
              data-testid={"findings-filter-status-" + st}
              aria-pressed={statusFilter.includes(st)}
              onClick={() => toggle(statusFilter, st, setStatusFilter)}
            >
              {st}
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            className="input min-h-10 flex-1 text-sm"
            placeholder="Suche (Finding, Mission, Finder, Fixer, Closer, Klasse)"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            data-testid="findings-search"
          />
          <input
            className="input min-h-10 sm:w-64 text-sm font-mono"
            placeholder="missionsfilter (mission_…)"
            value={missionFilter}
            onChange={(e) => setMissionFilter(e.target.value)}
            data-testid="findings-mission-filter"
          />
        </div>
      </div>

      {daten && daten.findings.length === 0 && !loading && (
        <div className="card p-4 text-sm text-jarvis-muted">
          Keine Findings für diese Filter — entweder gibt es keine offenen Belastungen oder der Filter ist zu eng.
        </div>
      )}

      <div className="space-y-2">
        {(daten?.findings ?? []).map((f) => (
          <div key={f.finding_id} className={`card p-3 ${f.severity === "CRITICAL" && f.status !== "CLOSED" ? "border-rose-500/50" : ""}`}>
            <button
              className="w-full text-left"
              data-testid={"finding-row-" + f.finding_id}
              onClick={() => setOffen(offen === f.finding_id ? null : f.finding_id)}
              aria-expanded={offen === f.finding_id}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${severityChipKlasse(f.severity)}`}
                  data-testid={"finding-severity-" + f.finding_id}
                >
                  {f.severity}
                </span>
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${statusChipKlasse(f.status)}`}>
                  {f.status}
                </span>
                <span className="text-[11px] text-jarvis-muted font-mono truncate max-w-[16rem]" title={f.mission_id}>
                  {f.mission_id}
                </span>
                <span className="text-[11px] text-jarvis-muted truncate max-w-[14rem]" title={f.mission_goal ?? ""}>
                  {f.mission_goal ?? "—"}
                </span>
                <span className="text-[10px] text-jarvis-muted ml-auto whitespace-nowrap">
                  {new Date(f.updated_at).toLocaleString("de-DE")}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-jarvis-muted mt-1.5">
                <span>
                  gefunden von <strong className="text-jarvis-text font-medium">{f.found_by}</strong>
                </span>
                {f.fixed_by && (
                  <span>
                    fix: <strong className="text-jarvis-text font-medium">{f.fixed_by}</strong>
                  </span>
                )}
                {f.closed_by && (
                  <span>
                    geschlossen von <strong className="text-jarvis-text font-medium">{f.closed_by}</strong>
                  </span>
                )}
                <span>Reattack: {reattackText(f)}</span>
              </div>
            </button>
            {offen === f.finding_id && (
              <div className="mt-3 space-y-1.5 text-xs border-t border-jarvis-border pt-2" data-testid="finding-detail">
                <div>
                  <strong>Finding:</strong> <span className="font-mono">{f.finding_id}</span> · <strong>Klasse:</strong> {f.finding_class}
                </div>
                <div>
                  <strong>Fix-Evidence:</strong>{" "}
                  {f.fix_evidence_id ? <span className="font-mono">{f.fix_evidence_id}</span> : "— (noch nicht geschlossen)"}
                </div>
                <div>
                  <strong>Reattack-Verdict:</strong>{" "}
                  {f.reattack_verdict_message_id ? <span className="font-mono">{f.reattack_verdict_message_id}</span> : "—"}
                </div>
                {f.challenge_fingerprint && (
                  <div>
                    <strong>Challenge-Fingerprint:</strong> <span className="font-mono">{f.challenge_fingerprint}</span>
                  </div>
                )}
                <div>
                  <strong>Referenzen:</strong> Thread {f.thread_id ?? "—"} · Challenge {f.challenge_message_id ?? "—"}
                </div>
                <div>
                  <strong>Zeiten:</strong> erstellt {new Date(f.created_at).toLocaleString("de-DE")} · aktualisiert{" "}
                  {new Date(f.updated_at).toLocaleString("de-DE")}
                </div>
                <div className="text-jarvis-muted pt-1">
                  Invarianten des Ledgers: found_by unveränderlich · kein Severity-Downgrade · kein Self-Close ·
                  Abschluss nur mit Fix-Evidence + unabhängigem Reattack-Verdict.
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
